import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, AlertTriangle, Bell, Target, TrendingUp, X, MessageSquare } from 'lucide-react';
import api from '@/lib/axios';
import { useNotification } from '@/contexts/NotificationContext';
import { useTranslation } from '@/contexts/TranslationContext';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface Notification {
  _id: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
  status?: 'READ' | 'UNREAD';
  read?: boolean;
  metadata?: {
    evaluationId?: string;
    evaluationType?: string;
    notificationSubtype?: string;
    scheduledDate?: string;
    documentId?: string;
    goalId?: string;
    trainingPlanId?: string;
    url?: string;
    token?: string;
    surveyId?: string;
    surveyTitle?: string;
    complaintId?: string;
    groupId?: string;
    messageId?: string;
    // mastery notifications
    position?: string;
    positions?: string[];
    track?: string;
    requestedByName?: string;
    kind?: string;
    pathwayType?: string;
  };
  // Legacy notifications carry evaluationId at the top level (see
  // server/src/models/Notification.js); newer ones use metadata.evaluationId.
  evaluationId?: string;
  relatedId?: string;
  relatedModel?: string;
  employee?: {
    name: string;
    position: string;
    department: string;
  };
}

interface NotificationListProps {
  onDismiss: () => void;
  isMobile?: boolean;
  compact?: boolean;
}

export function NotificationList({ onDismiss, isMobile = false, compact = false }: NotificationListProps) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await api.get('/api/notifications');
      setNotifications(response.data.notifications);
      setLoading(false);
    } catch (error) {
      showNotification('error', t('common.error'), t('notifications.failedToLoad'));
      setLoading(false);
    }
  }, [showNotification, t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleNotificationClick = async (notification: Notification) => {
    try {
      let navigated = false;
      
      // Always close the dropdown menu when clicking a notification
      onDismiss();
      
      // Navigate based on notification type - Fixed and simplified logic
      const notificationType = notification.type.toUpperCase();
      
      if (notificationType === 'EVALUATION' || notification.title.includes('360 Evaluation')) {
        // Handle evaluationId from metadata or direct property
        const evaluationId = notification.metadata?.evaluationId || notification.evaluationId || notification.relatedId;
        if (evaluationId) {
          const isLeadership360 = notification.metadata?.evaluationType === 'leadership360' || notification.title.includes('360 Evaluation');
          navigate(isLeadership360 ? `/leadership/360-evaluations/${evaluationId}` : `/evaluations/${evaluationId}`);
          navigated = true;
        }
      } else if (notification.relatedModel === 'LeadershipRoadmap') {
        // Area reminders + facilities/food-safety/kitchen alerts → the leadership
        // dashboard (the old /leadership/roadmaps page is deprecated).
        navigate('/leadership/dashboard');
        navigated = true;
      } else if (notification.relatedModel === 'ShiftReport' || notification.metadata?.url === '/shift-reports') {
        // Shift Reports "alerts owner" flags are type 'administrative' — route them before
        // the documentation branch below swallows every administrative notification.
        navigate('/shift-reports');
        navigated = true;
      } else if (notificationType === 'DOCUMENTATION' ||
                notificationType === 'ADMINISTRATIVE' ||
                notification.title.includes('Administrative Documentation')) {
        // For documentation notifications
        if (notification.metadata?.documentId) {
          navigate(`/documentation/${notification.metadata.documentId}`);
          navigated = true;
        } else if (notification.relatedId && notification.relatedModel === 'Documentation') {
          navigate(`/documentation/${notification.relatedId}`);
          navigated = true;
        } else {
          // If no specific document ID, navigate to documentation list
          navigate('/documentation');
          navigated = true;
        }
      } else if (notificationType === 'DISCIPLINARY') {
        // For disciplinary notifications - Fixed missing structure
        if (notification.metadata?.documentId) {
          navigate(`/disciplinary/${notification.metadata.documentId}`);
          navigated = true;
        } else if (notification.relatedId && notification.relatedModel === 'Disciplinary') {
          navigate(`/disciplinary/${notification.relatedId}`);
          navigated = true;
        } else {
          navigate('/disciplinary');
          navigated = true;
        }
      } else if (notificationType === 'GOAL') {
        // For goal notifications
        if (notification.metadata?.goalId) {
          navigate(`/goals/${notification.metadata.goalId}`);
          navigated = true;
        } else {
          navigate('/goals');
          navigated = true;
        }
      } else if (notificationType === 'TRAINING' || notificationType === 'TRAINING_ASSIGNED') {
        // For training notifications
        if (typeof notification.metadata?.url === 'string' && notification.metadata.url.startsWith('/')) {
          // Assessment assignments carry an explicit deep link (e.g. straight
          // into a self-assessment's take flow).
          navigate(notification.metadata.url);
          navigated = true;
        } else if (notification.metadata?.trainingPlanId) {
          navigate(`/training/plans/${notification.metadata.trainingPlanId}`);
          navigated = true;
        } else {
          navigate('/training');
          navigated = true;
        }
      } else if (notificationType === 'CUSTOMER_COMPLAINT' ||
                 notificationType === 'COMPLAINT_ALERT' ||
                 notificationType === 'GUEST_RECOVERY' ||
                 notification.title.includes('Guest Complaint') ||
                 notification.title.includes('Guest Recovery')) {
        // For customer complaint/guest recovery notifications
        if (notification.metadata?.complaintId) {
          navigate(`/customer-complaints?complaintId=${notification.metadata.complaintId}`);
          navigated = true;
        } else if (notification.relatedId && notification.relatedModel === 'CustomerComplaint') {
          navigate(`/customer-complaints?complaintId=${notification.relatedId}`);
          navigated = true;
        } else {
          navigate('/customer-complaints');
          navigated = true;
        }
      } else if (notificationType === 'SURVEY_INVITATION' ||
                 notification.title.includes('Survey')) {
        if (notification.metadata?.token) {
          navigate(`/survey/${notification.metadata.token}`);
          navigated = true;
        }
      } else if ((notificationType === 'TASK' && notification.relatedModel !== 'PersonalTask') ||
                 notification.title.includes('Mastery Request') ||
                 notification.title.includes('Pathway Step')) {
        // For task notifications (mastery requests, pathway completions, etc.)
        // Personal tasks also use type 'task' but live on the dashboard, not in
        // Team Development — they fall through to the metadata.url branch below.
        navigate('/team-development/overview');
        navigated = true;
      } else if (notificationType === 'CHAT_MESSAGE' ||
                 notificationType === 'CHAT_MENTION' ||
                 notificationType === 'CHAT_FILE' ||
                 notification.title.includes('chat') ||
                 notification.title.includes('Chat')) {
        // For chat notifications
        let chatUrl = '/chat';
        if (notification.metadata?.groupId) {
          chatUrl = `/chat?group=${notification.metadata.groupId}`;
          if (notification.metadata?.messageId) {
            chatUrl += `&messageId=${notification.metadata.messageId}`;
          }
        } else if (notification.relatedId) {
          chatUrl = `/chat?group=${notification.relatedId}`;
        }
        navigate(chatUrl);
        navigated = true;
      } else if (typeof notification.metadata?.url === 'string' && notification.metadata.url.startsWith('/')) {
        // Generic in-app deep link (e.g. reminder notifications carry metadata.url)
        navigate(notification.metadata.url);
        navigated = true;
      } else if (notification.relatedModel === 'PersonalTask') {
        // Personal-task notifications written before metadata.url was added.
        navigate('/dashboard');
        navigated = true;
      } else {
        // Handle other notification types or fallback to a default page
        // Default to documentation list for documentation-related notifications
        if (notification.title.includes('Documentation')) {
          navigate('/documentation');
          navigated = true;
        }
      }

      // Mark as read
      await api.post(`/api/notifications/${notification._id}/mark-read`);

      // Remove from list
      setNotifications(prev => prev.filter(n => n._id !== notification._id));
      
      // If we didn't navigate, at least indicate something is happening
      if (!navigated) {
        showNotification('info', t('notifications.processed'), t('notifications.notificationProcessed'));
      }
    } catch (error) {
      showNotification('error', t('common.error'), t('notifications.failedToProcess'));
    }
  };

  const handleDismissNotification = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation(); // Prevent the main click handler from firing

    try {
      // Optimistically remove from list immediately for better UX
      setNotifications(prev => prev.filter(n => n._id !== notification._id));

      // Don't call onDismiss() - we want to keep the dialog open when dismissing individual notifications
      // The dialog should only close when clicking on a notification to navigate

      // Mark as read on server (fire and forget for better UX)
      api.post(`/api/notifications/${notification._id}/mark-read`).catch(error => {
        console.error('Error marking notification as read:', error);
        // Could optionally re-add the notification back to the list on error
        // but for better UX, we'll keep it dismissed locally
      });

    } catch (error) {
      showNotification('error', t('common.error'), 'Failed to dismiss notification');
    }
  };

  const getNotificationIcon = (type: string) => {
    const upperType = type.toUpperCase();
    switch (upperType) {
      case 'EVALUATION':
        return <ClipboardList className="w-4 h-4 text-[#E51636]" />;
      case 'DISCIPLINARY':
        return <AlertTriangle className="w-4 h-4 text-[#E51636]" />;
      case 'GOAL':
        return <Target className="w-4 h-4 text-[#E51636]" />;
      case 'RECOGNITION':
        return <TrendingUp className="w-4 h-4 text-[#E51636]" />;
      case 'DOCUMENTATION':
      case 'ADMINISTRATIVE':
        return <ClipboardList className="w-4 h-4 text-[#E51636]" />;
      case 'SURVEY_INVITATION':
        return <MessageSquare className="w-4 h-4 text-[#E51636]" />;
      case 'TRAINING':
      case 'TRAINING_ASSIGNED':
        return <Target className="w-4 h-4 text-[#E51636]" />;
      default:
        return <Bell className="w-4 h-4 text-[#E51636]" />;
    }
  };

  const formatDate = (date: string) => {
    try {
      const dateObj = new Date(date);
      return format(dateObj, 'M/d/yyyy');
    } catch (error) {
      return date;
    }
  };

  // Simplified function to extract notification display text
  const getNotificationDisplayText = (notification: Notification) => {
    // Prefer a reliable, structured name for a concise "{Type} - {Name}" label.
    if (notification.employee?.name) {
      return `${getNotificationType(notification)} - ${notification.employee.name}`;
    }

    // Otherwise show the full message: it's the authoritative, human-readable
    // sentence the server wrote and already names the relevant item/person.
    // This replaces a brittle regex (/for ([A-Za-z\s]+)(?:\.|$|,)/) that tried to
    // pull a name out of the prose — it broke on suffixes like "(FOH)" and, on
    // failure, fell back to the bare title (e.g. just "Mastery Request"), dropping
    // all the useful detail. Falling back to the message is robust for every type.
    return notification.message || notification.title;
  };

  const getNotificationType = (notification: Notification) => {
    const title = notification.title;
    const type = notification.type.toUpperCase();

    if (title.includes('Documentation') || type === 'DOCUMENTATION') {
      return 'Documentation';
    } else if (title.includes('Disciplinary') || type === 'DISCIPLINARY') {
      return 'Disciplinary';
    } else if (title.includes('Evaluation') || type === 'EVALUATION') {
      return 'Evaluation';
    } else if (title.includes('Training') || type === 'TRAINING' || type === 'TRAINING_ASSIGNED') {
      return 'Training';
    } else if (title.includes('Goal') || type === 'GOAL') {
      return 'Goal';
    } else if (title.includes('Complaint') || type === 'CUSTOMER_COMPLAINT' || type === 'COMPLAINT_ALERT') {
      return 'Guest Recovery';
    } else if (title.includes('Survey') || type === 'SURVEY_INVITATION') {
      return 'Survey';
    } else {
      // Use first word of title as fallback
      return title.split(' ')[0];
    }
  };

  // Standardized function to check if notification is unread
  const isNotificationUnread = (notification: Notification) => {
    // Check both status field and read field for backward compatibility
    return notification.status === 'UNREAD' || !notification.read;
  };

  if (loading) {
    return (
      <div className={cn(
        "text-center text-gray-500",
        compact ? "p-2" : isMobile ? "p-6" : "p-4"
      )}>
        {t('common.loading')}
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className={cn(
        "text-center text-gray-500",
        compact ? "p-2" : isMobile ? "p-6" : "p-4"
      )}>
        {t('common.noNotifications')}
      </div>
    );
  }

  return (
    <div className={cn(
      "overflow-y-auto",
      compact && isMobile ? "max-h-[240px]" : compact ? "max-h-[30vh]" : isMobile ? "max-h-[calc(100vh-280px)] pb-16" : "max-h-[400px] py-2"
    )}>
      {notifications.map((notification) => (
        <div
          key={notification._id}
          className={cn(
            "relative hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer notification-list-item group",
            compact ? "p-3 border-b border-gray-100" : isMobile ? "p-4 border-b border-gray-100" : "px-4 py-3 border-b border-gray-100"
          )}
          onClick={() => handleNotificationClick(notification)}
        >
          <div className="flex items-start gap-3">
            <div className="relative flex-shrink-0">
              <div className={cn(
                "rounded-full bg-[#E51636]/10 flex items-center justify-center",
                compact ? "w-8 h-8" : isMobile ? "w-10 h-10" : "w-8 h-8"
              )}>
                {getNotificationIcon(notification.type)}
              </div>
              {isNotificationUnread(notification) && (
                <div className={cn(
                  "absolute bg-[#E51636] rounded-full notification-badge",
                  compact ? "-top-1 -right-1 w-3 h-3" : isMobile ? "-top-1 -right-1 w-4 h-4" : "-top-1 -right-1 w-3 h-3"
                )} />
              )}
            </div>
            <div className="flex-grow min-w-0">
              <div className="flex justify-between items-start gap-2">
                <div className="flex-grow min-w-0">
                  <p className={cn(
                    "font-semibold text-[#27251F] leading-tight",
                    compact ? "text-sm" : isMobile ? "text-base" : "text-sm"
                  )}>
                    {getNotificationDisplayText(notification)}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <p className={cn(
                    "text-[#27251F]/60 font-medium",
                    compact ? "text-xs" : isMobile ? "text-sm" : "text-xs"
                  )}>
                    {formatDate(notification.createdAt)}
                  </p>
                  {/* Dismiss Button */}
                  <button
                    onClick={(e) => handleDismissNotification(e, notification)}
                    className={cn(
                      "opacity-70 hover:opacity-100 transition-opacity duration-200 rounded-full hover:bg-gray-200 flex items-center justify-center touch-manipulation",
                      compact ? "w-6 h-6" : "w-7 h-7"
                    )}
                    aria-label="Dismiss notification"
                  >
                    <X className={cn(
                      "text-gray-400 hover:text-gray-600",
                      compact ? "w-3 h-3" : "w-4 h-4"
                    )} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}